import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {BACKEND_URL} from "../../../../configs";
import {
    MessageRole,
    PublicTranscriptMessageUi,
    PublicTranscriptUi
} from "../../../../api/generated";
import {Loader} from "../../../elements/loader/Loader";
import {TextInMarkdown} from "../../../elements/textInMarkdown/TextInMarkdown";
import {VehicleSlider} from "../page/chatHistory/components/assistantMessage/galleryMessage/components/vehicleSlider/VehicleSlider";
import {formatDateTime} from "../../../../utils/dateTimeUtil";
import styles from "./PublicTranscriptPage.module.css";

const INVALID_LINK_MESSAGE = "This link has either expired or does not exist. Please contact your administrator for more information";
const ASSISTANT_MESSAGE_COLOUR = "#3b566a";

export const PublicTranscriptPage: React.FC = () => {
    const {encodedLinkData} = useParams<{ encodedLinkData?: string }>();
    const [transcript, setTranscript] = useState<PublicTranscriptUi>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!encodedLinkData) {
            setErrorMessage(INVALID_LINK_MESSAGE);
            setLoading(false);
            return;
        }

        fetch(`${BACKEND_URL}/api/public/transcript/share/${encodeURIComponent(encodedLinkData)}`)
            .then(async response => {
                if (!response.ok) {
                    const text = await response.text();
                    throw new Error(text || INVALID_LINK_MESSAGE);
                }
                return response.json();
            })
            .then((payload: PublicTranscriptUi) => {
                setTranscript(payload);
                setErrorMessage(undefined);
            })
            .catch(() => {
                setErrorMessage(INVALID_LINK_MESSAGE);
                setTranscript(undefined);
            })
            .finally(() => setLoading(false));
    }, [encodedLinkData]);

    if (loading) {
        return <Loader/>;
    }

    if (errorMessage || !transcript) {
        return <main className={styles.page}>
            <div className={styles.notice}>{errorMessage || INVALID_LINK_MESSAGE}</div>
        </main>
    }

    return <main className={styles.page}>
        <div className={styles.shell}>
            <section className={styles.transcript}>
                {transcript.messages.map(message => <PublicTranscriptMessage key={message.id} message={message}/>)}
            </section>
        </div>
    </main>
}

const PublicTranscriptMessage: React.FC<{ message: PublicTranscriptMessageUi }> = ({message}) => {
    const isUserMessage = message.messageRole === MessageRole.User;
    const hasVehicleGallery = !isUserMessage && !!message.vehicleDataList?.length;
    const hasVideo = !isUserMessage && !!message.videoUrl;

    if (hasVehicleGallery) {
        return <div className={`${styles.messageRow} ${styles.assistantRow}`}>
            <div className={styles.assistantGalleryMessage}>
                {message.message && (
                    <div className={`${styles.messageShell} ${styles.assistantMessageShell}`}>
                        <PublicMessageTime timestamp={message.timestamp}/>
                        <span className={`${styles.bubble} ${styles.assistantBubble}`}>
                            <TextInMarkdown text={message.message}/>
                        </span>
                    </div>
                )}
                <div className={styles.galleryWrapper}>
                    <VehicleSlider
                        brandColour={ASSISTANT_MESSAGE_COLOUR}
                        vehicles={message.vehicleDataList!.map((vehicle, index) => ({
                            ...vehicle,
                            vin: vehicle.vin || vehicle.new_vehicle_id || vehicle.id?.toString() || `${message.id}-${index}`,
                            image_list: vehicle.image_list?.length ? vehicle.image_list : ["/no-image.jpeg"]
                        }))}
                    />
                </div>
            </div>
        </div>
    }

    if (hasVideo) {
        return <div className={`${styles.messageRow} ${styles.assistantRow}`}>
            <div className={styles.assistantVideoMessage}>
                <div className={styles.videoWrapper}>
                    <video controls src={message.videoUrl}/>
                </div>
                {message.message && (
                    <div className={`${styles.messageShell} ${styles.assistantMessageShell}`}>
                        <PublicMessageTime timestamp={message.timestamp}/>
                        <span className={`${styles.bubble} ${styles.assistantBubble}`}>
                            <TextInMarkdown text={message.message}/>
                        </span>
                    </div>
                )}
            </div>
        </div>
    }

    const rowClassName = `${styles.messageRow} ${isUserMessage ? styles.userRow : styles.assistantRow}`;
    const shellClassName = `${styles.messageShell} ${isUserMessage ? styles.userMessageShell : styles.assistantMessageShell}`;
    const bubbleClassName = `${styles.bubble} ${isUserMessage ? styles.userBubble : styles.assistantBubble}`;

    return <div className={rowClassName}>
        <div className={shellClassName}>
            {!isUserMessage && <PublicMessageTime timestamp={message.timestamp}/>}
            <span className={bubbleClassName}>
                {message.message && <TextInMarkdown text={message.message}/>}
                {!!message.offerCardDataList?.length && (
                    <div className={styles.vehicleGrid}>
                        {message.offerCardDataList.map((offer, index) => (
                            <div key={`${message.id}-offer-${offer.id || index}`} className={styles.vehicleCard}>
                                {offer.imageUrl && <img className={styles.vehicleImage} src={offer.imageUrl} alt="Offer"/>}
                                <div className={styles.vehicleBody}>
                                    <div className={styles.vehicleTitle}>{offer.title}</div>
                                    <div className={styles.vehicleMeta}>{offer.description}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </span>
            {isUserMessage && <PublicMessageTime timestamp={message.timestamp}/>}
        </div>
    </div>
}

const PublicMessageTime: React.FC<{ timestamp: string }> = ({timestamp}) => {
    const {time} = formatDateTime(timestamp);
    return <time className={styles.time}>{time}</time>
}
